import React, { useState, useCallback, useEffect, useRef } from 'react';
import { FileText, Swords, CheckSquare, MessageSquare, TrendingUp, GitBranch, ShieldCheck, Network, Sliders, ChevronLeft, ChevronRight } from 'lucide-react';

const LAYER_DATA = {
  5: {
    level: 'OS',
    category: 'AUTONOMOUS OPERATING SYSTEM',
    name: 'Frontier Deal OS™ Engine',
    badge: 'FULL-STACK ENGINE',
    badgeStyle: 'bg-[#F58220]/20 text-[#F58220]',
    title: 'Frontier Deal OS™',
    desc: 'The autonomous deal operating system unifying all four proprietary layers into a single full-stack engine — from sovereign SLMs to zero-prompt cockpit interfaces — deployed 100% in-tenant.',
    tags: [
      { text: 'Full-Stack OS', style: 'bg-white/10 text-slate-300' },
      { text: '100% In-Tenant', style: 'bg-white/10 text-slate-300' },
      { text: 'Sovereign AI', style: 'bg-[#F58220]/20 text-[#F58220]' },
    ],
  },
  4: {
    level: 'L4',
    category: 'INTERFACES & COCKPIT',
    name: 'DealSpace™ Cockpit',
    badge: 'ACTIVE TOP WAFER',
    badgeStyle: 'bg-[#F58220]/20 text-[#F58220]',
    title: 'DealSpace™ Cockpit',
    desc: 'Adversarial Bull vs. Bear deliberations, 18-factor IC investment rubrics, and publication-ready diligence decks synthesized at machine speed.',
    tags: [
      { text: '18-Factor Rubrics', style: 'bg-white/10 text-slate-300' },
      { text: 'Adversarial IC', style: 'bg-white/10 text-slate-300' },
      { text: '4.51x MOIC', style: 'bg-emerald-500/20 text-emerald-400' },
    ],
  },
  3: {
    level: 'L3',
    category: 'DAG ORCHESTRATION',
    name: 'SmartOrch™ DAG Orchestration',
    badge: 'PARALLEL PIPELINES',
    badgeStyle: 'bg-blue-500/20 text-blue-400',
    title: 'SmartOrch™ Multi-Agent DAG',
    desc: 'Directed acyclic graph pipelines executing parallel research, founder interrogations, and cross-source thesis stress-tests.',
    tags: [
      { text: 'Parallel DAG', style: 'bg-white/10 text-slate-300' },
      { text: 'Consensus Protocol', style: 'bg-white/10 text-slate-300' },
      { text: 'Zero Drift', style: 'bg-blue-500/20 text-blue-400' },
    ],
  },
  2: {
    level: 'L2',
    category: 'CONTEXT GRAPH',
    name: 'OmniContext™ Sovereign Graph',
    badge: '1,400+ VDR FILES',
    badgeStyle: 'bg-emerald-500/20 text-emerald-400',
    title: 'OmniContext™ Deal Graph',
    desc: 'Sovereign transaction memory graph reconciling multi-format data rooms with unbreakable cell-to-source provenance and air-gapped isolation.',
    tags: [
      { text: 'Cell-to-Source', style: 'bg-white/10 text-slate-300' },
      { text: 'Air-Gapped Enclave', style: 'bg-white/10 text-slate-300' },
      { text: 'Zero MNPI Leak', style: 'bg-purple-500/20 text-purple-400' },
    ],
  },
  1: {
    level: 'L1',
    category: 'STRATEGIC DOMAIN SLMs',
    name: 'StrategyCore™ SLMs & Off-LLM Math',
    badge: 'OFF-LLM DETERMINISM',
    badgeStyle: 'bg-[#F58220]/20 text-[#F58220]',
    title: 'StrategyCore™ Reasoning SLMs',
    desc: 'Calibrated on 20+ years of institutional M&A heuristics, executing deterministic off-LLM financial math without hallucinations.',
    tags: [
      { text: 'Deterministic LBO/DCF', style: 'bg-white/10 text-slate-300' },
      { text: '20y Heuristics', style: 'bg-white/10 text-slate-300' },
      { text: 'Zero Hallucinations', style: 'bg-emerald-500/20 text-emerald-400' },
    ],
  },
};

const WAFFLE_TILES = [
  { Icon: FileText, title: 'IC Memo', sub: 'Synthesis' },
  { Icon: Swords, title: 'Bull vs Bear', sub: 'Deliberation' },
  { Icon: CheckSquare, title: '18-Factor', sub: 'IC Rubric' },
  { Icon: MessageSquare, title: 'Dally Chat', sub: 'Interrogate' },
  { Icon: TrendingUp, title: 'Growth Thesis', sub: 'Value Creation', highlight: true },
  { Icon: GitBranch, title: 'Provenance', sub: 'Cell-Level' },
  { Icon: ShieldCheck, title: 'Moat Audit', sub: 'Defensibility' },
  { Icon: Network, title: 'Strategic JVs', sub: '& Alliances' },
  { Icon: Sliders, title: 'Scenario Trees', sub: 'Downside Floor' },
];

const SLAB_Z = { 1: 0, 2: 42, 3: 84, 4: 126, 5: 168 };

const STACK_CSS = `
@keyframes stackFloat {
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-8px); }
}
@keyframes stackFloatShadow {
  0%, 100% { opacity: 0.14; transform: scale(1); }
  50% { opacity: 0.08; transform: scale(1.06); }
}
@keyframes glowPulse {
  0%, 100% { opacity: 0.4; }
  50% { opacity: 0.75; }
}
@keyframes shineSwipe {
  0% { transform: translateX(-120%) rotate(20deg); }
  100% { transform: translateX(120%) rotate(20deg); }
}
@keyframes aosShimmer {
  0%, 100% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
}
@keyframes aosGlow {
  0%, 100% { opacity: 0.55; transform: scale(1); }
  50% { opacity: 0.95; transform: scale(1.08); }
}
.waffle-tile {
  background: linear-gradient(135deg, rgba(255,255,255,0.96) 0%, rgba(245,248,252,0.88) 100%);
  border: 1.5px solid rgba(255,255,255,1);
  box-shadow: 0 6px 14px -3px rgba(0,0,0,0.06), inset 0 1px 2px rgba(255,255,255,1), inset 0 -2px 4px rgba(0,0,0,0.03);
  border-radius: 16px;
  padding: 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
}
.waffle-tile:hover {
  transform: translateZ(8px) scale(1.03);
  border-color: rgba(245, 130, 32, 0.6);
  box-shadow: 0 12px 22px -5px rgba(245,130,32,0.18), inset 0 1px 2px rgba(255,255,255,1), inset 0 -2px 4px rgba(0,0,0,0.03);
}
.waffle-tile.is-amber {
  background: rgba(255, 246, 238, 0.6);
  border-color: rgba(245, 130, 32, 0.3);
}
.stack-tag {
  background: linear-gradient(135deg, rgba(255,255,255,0.97) 0%, rgba(245,248,252,0.9) 100%);
  border: 1.5px solid rgba(255,255,255,1);
  box-shadow: 0 4px 10px -3px rgba(0,0,0,0.08), inset 0 1px 2px rgba(255,255,255,1), inset 0 -2px 4px rgba(0,0,0,0.03);
  border-radius: 10px;
  padding: 5px 11px;
  white-space: nowrap;
  transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
}
.stack-tag:hover {
  transform: translateZ(6px) scale(1.05);
  border-color: rgba(245, 130, 32, 0.55);
  box-shadow: 0 9px 18px -4px rgba(245,130,32,0.18), inset 0 1px 2px rgba(255,255,255,1);
}
.slab-dim-content {
  filter: blur(2px);
  opacity: 0.7;
  transition: filter 0.45s ease, opacity 0.45s ease;
}
`;

export const ArchitectureSection = () => {
  const [activeLayer, setActiveLayer] = useState(5);
  const [hoveredLayer, setHoveredLayer] = useState(null);
  const [mounted, setMounted] = useState(false);
  const sectionRef = useRef(null);
  const stackRef = useRef(null);
  const mouseRef = useRef({ x: 0, y: 0 });
  const smoothMouse = useRef({ x: 0, y: 0 });
  const rafRef = useRef(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setMounted(true); },
      { threshold: 0.15 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!mounted) return;
    const el = stackRef.current;
    if (!el) return;

    const onMove = (e) => {
      const rect = el.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      mouseRef.current.x = (e.clientX - cx) / (rect.width / 2);
      mouseRef.current.y = (e.clientY - cy) / (rect.height / 2);
    };

    const onLeave = () => {
      mouseRef.current.x = 0;
      mouseRef.current.y = 0;
    };

    const tick = () => {
      const lerpF = 0.06;
      smoothMouse.current.x += (mouseRef.current.x - smoothMouse.current.x) * lerpF;
      smoothMouse.current.y += (mouseRef.current.y - smoothMouse.current.y) * lerpF;

      if (el) {
        const rx = smoothMouse.current.y * -4;
        const ry = smoothMouse.current.x * 5;
        el.style.setProperty('--mx', `${ry}deg`);
        el.style.setProperty('--my', `${rx}deg`);
      }
      rafRef.current = requestAnimationFrame(tick);
    };

    window.addEventListener('mousemove', onMove, { passive: true });
    el.addEventListener('mouseleave', onLeave);
    rafRef.current = requestAnimationFrame(tick);

    return () => {
      window.removeEventListener('mousemove', onMove);
      el.removeEventListener('mouseleave', onLeave);
      cancelAnimationFrame(rafRef.current);
    };
  }, [mounted]);

  const selectLayer = useCallback((n) => setActiveLayer(n), []);

  const vis = (n) => mounted && n <= activeLayer;
  const act = (n) => n === activeLayer;
  const hov = (n) => hoveredLayer === n && !act(n);

  const slabTransform = (n) => {
    const isVis = vis(n);
    const isAct = act(n);
    const isHov = hov(n);
    const z = SLAB_Z[n] + (isAct ? 30 : isHov ? 15 : 0);
    const s = isAct ? 1.04 : isHov ? 1.01 : 0.97;
    if (!isVis) return `translateY(-500px) rotateX(58deg) rotateZ(-45deg) translateZ(${SLAB_Z[n]}px) scale(0.85)`;
    return `translateY(0px) rotateX(58deg) rotateZ(-45deg) translateZ(${z}px) scale(${s})`;
  };

  const slabTransition = (n) => {
    const isVis = vis(n);
    const showDelay = (n - 1) * 140;
    const hideDelay = (5 - n) * 100;
    const delay = isVis ? showDelay : hideDelay;
    return `transform 0.9s cubic-bezier(0.22, 1.0, 0.36, 1) ${delay}ms, opacity 0.6s ease ${delay}ms, box-shadow 0.5s cubic-bezier(0.22, 1, 0.36, 1), border-color 0.4s ease, filter 0.4s ease`;
  };

  const slabStyle = (n) => {
    const isVis = vis(n);
    const isAct = act(n);
    const isHov = hov(n);

    const base = {
      position: 'absolute',
      inset: 0,
      borderRadius: '36px',
      padding: '24px',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      transformStyle: 'preserve-3d',
      transform: slabTransform(n),
      transition: slabTransition(n),
      opacity: isVis ? (isAct ? 1 : isHov ? 0.92 : 0.78) : 0,
      zIndex: n * 10,
      cursor: 'pointer',
      pointerEvents: isVis ? 'auto' : 'none',
      willChange: 'transform, opacity',
      overflow: 'hidden',
      filter: isAct ? 'brightness(1)' : isHov ? 'brightness(1.02)' : 'brightness(0.97)',
    };

    if (n === 5) {
      return {
        ...base,
        background: isAct
          ? 'linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(248,250,255,0.95) 100%)'
          : 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(245,248,252,0.88) 100%)',
        border: isAct ? '2px solid rgba(15,23,42,0.55)' : isHov ? '2px solid rgba(200,210,230,0.8)' : '2px solid rgba(255,255,255,0.85)',
        boxShadow: isAct
          ? '0 0 50px -5px rgba(15,23,42,0.3), 0 40px 70px -15px rgba(15,23,42,0.2), inset 0 2px 4px rgba(255,255,255,0.95), inset 0 -3px 8px rgba(0,0,0,0.04)'
          : isHov
          ? '0 0 25px -5px rgba(150,170,210,0.15), 0 35px 60px -12px rgba(15,23,42,0.15), inset 0 2px 4px rgba(255,255,255,0.9), inset 0 -3px 8px rgba(0,0,0,0.03)'
          : '0 30px 55px -12px rgba(15,23,42,0.12), inset 0 2px 4px rgba(255,255,255,0.85), inset 0 -3px 8px rgba(0,0,0,0.02)',
      };
    }

    if (n === 1) {
      return {
        ...base,
        background: 'linear-gradient(135deg, rgba(148,163,184,0.95) 0%, rgba(168,182,200,0.92) 100%)',
        border: isAct ? '2px solid rgba(15,23,42,0.55)' : isHov ? '2px solid rgba(200,210,230,0.8)' : '2px solid rgba(255,255,255,0.6)',
        boxShadow: isAct
          ? '0 0 50px -5px rgba(15,23,42,0.3), 0 35px 60px -12px rgba(15,23,42,0.18), inset 0 2px 4px rgba(255,255,255,0.5), inset 0 -3px 8px rgba(0,0,0,0.06)'
          : isHov
          ? '0 0 25px -5px rgba(150,170,210,0.15), 0 30px 55px -12px rgba(15,23,42,0.14), inset 0 2px 4px rgba(255,255,255,0.4), inset 0 -3px 8px rgba(0,0,0,0.04)'
          : '0 25px 50px -12px rgba(15,23,42,0.1), inset 0 2px 4px rgba(255,255,255,0.35), inset 0 -3px 8px rgba(0,0,0,0.03)',
      };
    }

    return {
      ...base,
      background: isAct
        ? 'linear-gradient(135deg, rgba(255,255,255,0.96) 0%, rgba(245,248,255,0.82) 100%)'
        : 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(238,242,250,0.7) 100%)',
      backdropFilter: 'blur(8px)',
      border: isAct ? '2px solid rgba(15,23,42,0.55)' : isHov ? '2px solid rgba(200,210,230,0.8)' : '2px solid rgba(255,255,255,0.85)',
      boxShadow: isAct
        ? '0 0 50px -5px rgba(15,23,42,0.3), 0 35px 60px -12px rgba(15,23,42,0.18), inset 0 2px 4px rgba(255,255,255,0.95), inset 0 -3px 8px rgba(0,0,0,0.04)'
        : isHov
        ? '0 0 25px -5px rgba(150,170,210,0.15), 0 30px 55px -12px rgba(15,23,42,0.14), inset 0 2px 4px rgba(255,255,255,0.9), inset 0 -3px 8px rgba(0,0,0,0.03)'
        : '0 25px 50px -12px rgba(15,23,42,0.1), inset 0 2px 4px rgba(255,255,255,0.85), inset 0 -3px 8px rgba(0,0,0,0.02)',
    };
  };

  const shineOverlay = (n) => ({
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    borderRadius: '36px',
    pointerEvents: 'none',
    overflow: 'hidden',
    opacity: act(n) ? 0.5 : 0,
    transition: 'opacity 0.6s ease',
  });

  return (
    <section ref={sectionRef} id="deal-aos" className="scroll-mt-16 pt-24 pb-10 bg-[#FBFBFB] relative overflow-hidden border-b border-slate-200">
      <style>{STACK_CSS}</style>
      <div className="w-full max-w-[1680px] mx-auto px-4 sm:px-8 lg:px-12">

        {/* Header */}
        <div className="text-center max-w-4xl mx-auto mb-16">
          <span className="text-xs font-mono uppercase tracking-widest text-[#F58220] font-semibold block mb-2">
            PROPRIETARY ARCHITECTURE
          </span>
          <h2 className="text-3xl sm:text-5xl lg:text-6xl font-extrabold text-[#0F172A] tracking-tight mb-4">
            Introducing the Frontier Deal OS&#8482;
          </h2>
          <p className="text-slate-600 text-lg sm:text-xl">
            The cognitive multi-agent operating system for high-stakes strategy and deals. Full-stack proprietary IP—deployed 100% in-tenant.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 xl:gap-12 items-center">

          {/* LEFT: HUD Navigation Rail (desktop) */}
          <div className="hidden lg:block lg:col-span-5 relative z-20">
            <div className="space-y-3 max-w-md">
              {[5, 4, 3, 2, 1].map((n) => {
                const d = LAYER_DATA[n];
                const isAct = act(n);
                return (
                  <div key={n}>
                    {!isAct ? (
                      <button
                        onClick={() => selectLayer(n)}
                        className="w-full flex p-3 rounded-full bg-[#0F172A]/90 hover:bg-[#0F172A] text-slate-300 hover:text-white border border-white/10 shadow-sm transition-all duration-200 items-center justify-between px-5 group"
                      >
                        <div className="flex items-center gap-2.5">
                          <span className="w-2 h-2 rounded-full bg-slate-500 group-hover:bg-[#F58220]/60 transition-colors duration-300" />
                          <span className="text-xs font-bold tracking-tight text-white">{d.name}</span>
                        </div>
                        <span className="text-[10px] font-mono text-slate-400 group-hover:text-[#F58220] transition-colors duration-300">+</span>
                      </button>
                    ) : (
                      <div className="p-5 rounded-2xl bg-[#0F172A] border border-[#F58220]/40 shadow-2xl text-white space-y-3 transition-all duration-500 ease-out">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-[#F58220] animate-pulse" />
                            <span className="font-mono text-[10px] font-bold uppercase tracking-wider text-[#F58220]">
                              {d.level} // {d.category}
                            </span>
                          </div>
                          <span className={`text-[9px] font-mono ${d.badgeStyle} px-2 py-0.5 rounded-full font-bold`}>
                            {d.badge}
                          </span>
                        </div>
                        <div className="font-bold text-lg text-white leading-tight">{d.title}</div>
                        <p className="text-xs text-slate-300 leading-relaxed">{d.desc}</p>
                        <div className="pt-2 flex flex-wrap gap-1.5 border-t border-slate-800">
                          {d.tags.map((t) => (
                            <span key={t.text} className={`text-[9px] font-mono ${t.style} px-2 py-0.5 rounded-full`}>
                              {t.text}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* RIGHT: CSS 3D Isometric Stack */}
          <div
            ref={stackRef}
            className="lg:col-span-7 flex justify-center items-center h-[380px] sm:h-[460px] lg:h-auto lg:pt-56 lg:pb-16 scale-[0.74] sm:scale-[0.9] lg:scale-100 origin-center lg:origin-top"
            style={{
              perspective: '2200px',
              perspectiveOrigin: '50% 40%',
            }}
          >
            <div
              className="relative w-[336px] lg:w-[416px] h-[336px] lg:h-[416px]"
              style={{
                transformStyle: 'preserve-3d',
                transform: 'rotateY(var(--mx, 0deg)) rotateX(var(--my, 0deg))',
                transition: 'transform 0.1s ease-out',
              }}
            >
            <div
              className="absolute inset-0"
              style={{
                transformStyle: 'preserve-3d',
                animation: mounted ? 'stackFloat 6s cubic-bezier(0.45, 0, 0.55, 1) infinite' : 'none',
              }}
            >
              {/* Floor shadow — breathes with the float */}
              <div
                className="absolute inset-4 rounded-full pointer-events-none"
                style={{
                  background: 'radial-gradient(ellipse at center, rgba(15,23,42,0.16) 0%, transparent 72%)',
                  filter: 'blur(35px)',
                  animation: mounted ? 'stackFloatShadow 6s cubic-bezier(0.45, 0, 0.55, 1) infinite' : 'none',
                }}
              />

              {/* Reflection plane */}
              <div
                className="absolute pointer-events-none"
                style={{
                  left: '10%',
                  right: '10%',
                  bottom: '-5%',
                  height: '50%',
                  transform: 'rotateX(58deg) rotateZ(-45deg) translateZ(-10px) scaleY(-0.3)',
                  background: 'linear-gradient(to bottom, rgba(200,210,230,0.08), transparent)',
                  filter: 'blur(12px)',
                  opacity: 0.5,
                  borderRadius: '36px',
                }}
              />

              {/* Active layer glow ring */}
              <div
                className="absolute inset-0 rounded-[36px] pointer-events-none"
                style={{
                  transform: `rotateX(58deg) rotateZ(-45deg) translateZ(${SLAB_Z[activeLayer] + 31}px)`,
                  boxShadow: '0 0 70px 10px rgba(15,23,42,0.12)',
                  transition: 'transform 0.9s cubic-bezier(0.22, 1.0, 0.36, 1)',
                  animation: 'glowPulse 3s ease-in-out infinite',
                }}
              />

              {/* L1: StrategyCore™ Dark Crystal Substrate */}
              <div
                onClick={() => selectLayer(1)}
                onMouseEnter={() => setHoveredLayer(1)}
                onMouseLeave={() => setHoveredLayer(null)}
                style={slabStyle(1)}
              >
                <div style={shineOverlay(1)}>
                  <div style={{
                    position: 'absolute', top: '-50%', left: '-50%',
                    width: '200%', height: '200%',
                    background: 'linear-gradient(90deg, transparent 30%, rgba(255,255,255,0.06) 50%, transparent 70%)',
                    animation: act(1) ? 'shineSwipe 3s ease-in-out infinite' : 'none',
                  }} />
                </div>
                <div
                  className="absolute inset-0 pointer-events-none rounded-[36px]"
                  style={{ background: 'radial-gradient(rgba(255,255,255,0.03) 1px, transparent 1px)', backgroundSize: '16px 16px' }}
                />
                <div className="flex items-center justify-between relative z-10">
                  <div className="flex items-center gap-2">
                    <span className="px-2 py-0.5 rounded-lg bg-[#F58220]/20 border border-[#F58220]/40 text-[10px] font-mono font-bold text-[#F58220]">L1</span>
                    <span className="font-bold text-xs tracking-tight text-slate-900 font-mono">StrategyCore&#8482;</span>
                  </div>
                  <span className="text-[9px] font-mono bg-slate-700/15 text-slate-700 px-2 py-0.5 rounded-full hidden sm:inline">L1 // STRATEGIC REASONING SLMS</span>
                </div>
                <div className={`flex-1 flex flex-col items-center justify-center relative z-10 ${act(1) ? '' : 'slab-dim-content'}`}>
                  <span className="text-base sm:text-lg font-extrabold text-slate-800 tracking-tight font-mono">Off-LLM Deterministic Math</span>
                </div>
                <div className={`flex items-center justify-center flex-wrap gap-1.5 border-t border-slate-400/30 pt-3 relative z-10 ${act(1) ? '' : 'slab-dim-content'}`}>
                  <span className="stack-tag text-[9px] font-mono font-semibold text-slate-600">20Y M&amp;A Heuristics</span>
                  <span className="stack-tag text-[9px] font-mono font-semibold text-slate-600">CDD Hypothesis Logic</span>
                  <span className="stack-tag text-[9px] font-mono font-semibold text-[#F58220]">100% Deterministic</span>
                </div>
              </div>

              {/* L2: OmniContext™ Sovereign Memory Graph */}
              <div
                onClick={() => selectLayer(2)}
                onMouseEnter={() => setHoveredLayer(2)}
                onMouseLeave={() => setHoveredLayer(null)}
                style={slabStyle(2)}
              >
                <div style={shineOverlay(2)}>
                  <div style={{
                    position: 'absolute', top: '-50%', left: '-50%',
                    width: '200%', height: '200%',
                    background: 'linear-gradient(90deg, transparent 30%, rgba(255,255,255,0.08) 50%, transparent 70%)',
                    animation: act(2) ? 'shineSwipe 3s ease-in-out infinite' : 'none',
                  }} />
                </div>
                <div className="flex items-center justify-between relative z-10">
                  <div className="flex items-center gap-2">
                    <span className="px-2 py-0.5 rounded-lg bg-slate-900/10 border border-slate-300 text-[10px] font-mono font-bold text-slate-800">L2</span>
                    <span className="font-bold text-xs tracking-tight text-slate-900 font-mono">OmniContext&#8482;</span>
                  </div>
                  <span className="text-[9px] font-mono bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 rounded-full font-semibold hidden sm:inline">L2 // SOVEREIGN DEAL GRAPH</span>
                </div>
                <div className={`flex-1 flex flex-col items-center justify-center relative z-10 ${act(2) ? '' : 'slab-dim-content'}`}>
                  <span className="text-base sm:text-lg font-extrabold text-slate-800 tracking-tight font-mono">Multi-Format VDR Ingest</span>
                </div>
                <div className={`flex items-center justify-center flex-wrap gap-1.5 border-t border-slate-200/80 pt-3 relative z-10 ${act(2) ? '' : 'slab-dim-content'}`}>
                  <span className="stack-tag text-[9px] font-mono font-semibold text-slate-600">Cell-to-Source Lineage</span>
                  <span className="stack-tag text-[9px] font-mono font-semibold text-slate-600">Zero Context Drift</span>
                  <span className="stack-tag text-[9px] font-mono font-semibold text-[#F58220]">Air-Gapped Enclave</span>
                </div>
              </div>

              {/* L3: SmartOrch™ Parallel DAG Orchestration */}
              <div
                onClick={() => selectLayer(3)}
                onMouseEnter={() => setHoveredLayer(3)}
                onMouseLeave={() => setHoveredLayer(null)}
                style={slabStyle(3)}
              >
                <div style={shineOverlay(3)}>
                  <div style={{
                    position: 'absolute', top: '-50%', left: '-50%',
                    width: '200%', height: '200%',
                    background: 'linear-gradient(90deg, transparent 30%, rgba(255,255,255,0.08) 50%, transparent 70%)',
                    animation: act(3) ? 'shineSwipe 3s ease-in-out infinite' : 'none',
                  }} />
                </div>
                <div className="flex items-center justify-between relative z-10">
                  <div className="flex items-center gap-2">
                    <span className="px-2 py-0.5 rounded-lg bg-slate-900/10 border border-slate-300 text-[10px] font-mono font-bold text-slate-800">L3</span>
                    <span className="font-bold text-xs tracking-tight text-slate-900 font-mono">SmartOrch&#8482;</span>
                  </div>
                  <span className="text-[9px] font-mono bg-blue-50 text-blue-700 border border-blue-200 px-2 py-0.5 rounded-full font-semibold hidden sm:inline">MULTI-AGENT DAG CHOREOGRAPHY</span>
                </div>
                <div className={`flex-1 flex flex-col items-center justify-center relative z-10 ${act(3) ? '' : 'slab-dim-content'}`}>
                  <span className="text-base sm:text-lg font-extrabold text-slate-800 tracking-tight font-mono">Context-Locked DAGs</span>
                </div>
                <div className={`flex items-center justify-center flex-wrap gap-1.5 border-t border-slate-200/80 pt-3 relative z-10 ${act(3) ? '' : 'slab-dim-content'}`}>
                  <span className="stack-tag text-[9px] font-mono font-semibold text-slate-600">Bull vs. Bear Protocols</span>
                  <span className="stack-tag text-[9px] font-mono font-semibold text-slate-600">48h CDD Verdicts</span>
                  <span className="stack-tag text-[9px] font-mono font-semibold text-[#F58220]">Consensus Protocol Active</span>
                </div>
              </div>

              {/* L4: DealSpace™ Cockpit */}
              <div
                onClick={() => selectLayer(4)}
                onMouseEnter={() => setHoveredLayer(4)}
                onMouseLeave={() => setHoveredLayer(null)}
                style={slabStyle(4)}
              >
                <div style={shineOverlay(4)}>
                  <div style={{
                    position: 'absolute', top: '-50%', left: '-50%',
                    width: '200%', height: '200%',
                    background: 'linear-gradient(90deg, transparent 30%, rgba(255,255,255,0.08) 50%, transparent 70%)',
                    animation: act(4) ? 'shineSwipe 3s ease-in-out infinite' : 'none',
                  }} />
                </div>
                <div className="flex items-center justify-between relative z-10 pb-3 border-b border-slate-200/80">
                  <div className="flex items-center gap-2">
                    <span className="px-2.5 py-0.5 rounded-lg bg-[#F58220] text-[10px] font-mono font-bold text-white shadow-sm">L4</span>
                    <span className="font-extrabold text-xs tracking-tight text-slate-900 font-mono">DealSpace&#8482;</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-[9px] font-mono font-bold text-[#F58220] bg-[#F58220]/15 px-2 py-0.5 rounded-full">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#F58220] animate-ping" />
                    <span className="hidden sm:inline">COCKPIT INTERFACES</span>
                  </div>
                </div>
                <div className={`grid grid-cols-3 gap-2.5 my-auto relative z-10 ${act(4) ? '' : 'slab-dim-content'}`}>
                  {WAFFLE_TILES.map((tile) => (
                    <div key={tile.title} className={`waffle-tile group/tile ${tile.highlight ? 'is-amber' : ''}`}>
                      <tile.Icon size={16} className={`mb-1 ${tile.highlight ? 'text-[#F58220]' : 'text-slate-700 group-hover/tile:text-[#F58220] transition-colors'}`} />
                      <span className={`text-[10px] font-bold leading-tight ${tile.highlight ? 'text-slate-900' : 'text-slate-800'}`}>{tile.title}</span>
                      <span className={`text-[8px] font-mono mt-0.5 ${tile.highlight ? 'text-[#F58220] font-semibold' : 'text-slate-500'}`}>{tile.sub}</span>
                    </div>
                  ))}
                </div>
                <div className={`flex items-center justify-between text-[10px] font-mono text-slate-700 border-t border-slate-200/80 pt-2.5 relative z-10 ${act(4) ? '' : 'slab-dim-content'}`}>
                  <span className="font-semibold text-slate-900">48-Hour IC Turnaround</span>
                  <span className="text-[#F58220] font-bold">Audit-Grade Lineage</span>
                </div>
              </div>

              {/* L5: Frontier Deal OS™ Engine — The Lid */}
              <div
                onClick={() => selectLayer(5)}
                onMouseEnter={() => setHoveredLayer(5)}
                onMouseLeave={() => setHoveredLayer(null)}
                style={slabStyle(5)}
              >
                <div style={shineOverlay(5)}>
                  <div style={{
                    position: 'absolute', top: '-50%', left: '-50%',
                    width: '200%', height: '200%',
                    background: 'linear-gradient(90deg, transparent 30%, rgba(255,255,255,0.08) 50%, transparent 70%)',
                    animation: act(5) ? 'shineSwipe 3s ease-in-out infinite' : 'none',
                  }} />
                </div>
                <div className="flex-1 flex items-center justify-center relative z-10">
                  <div className="relative flex items-center justify-center group/aos cursor-pointer">
                    {/* Soft amber glow behind the wordmark */}
                    <div
                      className="absolute -inset-x-8 -inset-y-5 rounded-full bg-[#F58220]/15 blur-2xl pointer-events-none"
                      style={{ animation: 'aosGlow 3.5s ease-in-out infinite' }}
                    />
                    <span
                      className="relative text-xl sm:text-2xl lg:text-[26px] font-black tracking-tight font-mono bg-clip-text text-transparent transition-transform duration-300 group-hover/aos:scale-110"
                      style={{
                        backgroundImage: 'linear-gradient(110deg, #0F172A 0%, #334155 26%, #F58220 50%, #334155 74%, #0F172A 100%)',
                        backgroundSize: '220% 100%',
                        animation: 'aosShimmer 4.5s ease-in-out infinite',
                        filter: 'drop-shadow(0 1px 1px rgba(255,255,255,0.7))',
                      }}
                    >
                      Frontier Deal OS<span style={{ WebkitTextFillColor: '#F58220', color: '#F58220' }}>&#8482;</span>
                    </span>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>

          {/* MOBILE: active-layer caption + prev/next controls */}
          <div className="lg:hidden mt-4 px-1 relative z-10">
            <div className="rounded-2xl bg-[#0F172A] border border-[#F58220]/40 shadow-2xl text-white p-5 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-[#F58220] animate-pulse" />
                  <span className="font-mono text-[10px] font-bold uppercase tracking-wider text-[#F58220]">
                    {LAYER_DATA[activeLayer].level} // {LAYER_DATA[activeLayer].category}
                  </span>
                </div>
                <span className={`text-[9px] font-mono ${LAYER_DATA[activeLayer].badgeStyle} px-2 py-0.5 rounded-full font-bold`}>
                  {LAYER_DATA[activeLayer].badge}
                </span>
              </div>
              <div className="font-bold text-lg text-white leading-tight">{LAYER_DATA[activeLayer].title}</div>
              <p className="text-xs text-slate-300 leading-relaxed">{LAYER_DATA[activeLayer].desc}</p>
              <div className="pt-2 flex flex-wrap gap-1.5 border-t border-slate-800">
                {LAYER_DATA[activeLayer].tags.map((t) => (
                  <span key={t.text} className={`text-[9px] font-mono ${t.style} px-2 py-0.5 rounded-full`}>{t.text}</span>
                ))}
              </div>
            </div>
            <div className="flex items-center justify-between mt-4">
              <button
                onClick={() => selectLayer(Math.min(5, activeLayer + 1))}
                disabled={activeLayer === 5}
                aria-label="Previous layer"
                className="w-11 h-11 rounded-full bg-[#0F172A] text-white flex items-center justify-center border border-white/10 active:scale-95 transition disabled:opacity-30 disabled:active:scale-100"
              >
                <ChevronLeft size={18} />
              </button>
              <div className="flex items-center gap-2">
                {[5, 4, 3, 2, 1].map((n) => (
                  <button
                    key={n}
                    onClick={() => selectLayer(n)}
                    aria-label={`Layer ${n}`}
                    className={`w-2 h-2 rounded-full transition-colors ${n === activeLayer ? 'bg-[#F58220]' : 'bg-slate-300'}`}
                  />
                ))}
              </div>
              <button
                onClick={() => selectLayer(Math.max(1, activeLayer - 1))}
                disabled={activeLayer === 1}
                aria-label="Next layer"
                className="w-11 h-11 rounded-full bg-[#0F172A] text-white flex items-center justify-center border border-white/10 active:scale-95 transition disabled:opacity-30 disabled:active:scale-100"
              >
                <ChevronRight size={18} />
              </button>
            </div>
          </div>

      </div>
      </div>
    </section>
  );
};
